<template>
  <div class="input-with-button" :style="containerInlineStyle">
    <span class="icon-prefix" v-if="icon">
      <v-icon>{{ icon }}</v-icon>
    </span>
    <input
      :type="typeInput"
      v-model="localValue"
      :placeholder="label"
      :maxlength="maxlength"
      @input="handleInput"
      @keyup.enter="handleButtonClick"
      class="input-field no-spin"
      :style="inputInlineStyle"
    />
    <v-select
      v-if="hasSelect"
      dense
      outlined
      hide-details
      class="inline-select"
      :items="selectItems"
      v-model="selectModel"
      :item-text="selectItemText || undefined"
      :item-value="selectItemValue || undefined"
      :style="{ width: selectWidth }"
    />
    <v-tooltip bottom>
      <template v-slot:activator="{ on, attrs }" v-if="isButton">
        <button
          v-bind="attrs"
          v-on="on"
          @click="handleButtonClick"
          class="input-button"
          :style="buttonInlineStyle"
          :disabled="buttonDisabled"
        >
          <v-icon color="#ffffff">{{ iconButton }}</v-icon>
        </button>
      </template>
      <span>{{ tooltip }}</span>
    </v-tooltip>
  </div>
</template>

<script>
import latinCarNumberMixin from '@/mixins/latinCarNumberMixin';

export default {
  name: 'BaseTextField',
  mixins: [latinCarNumberMixin],
  props: {
    value: {
      type: String,
      required: true,
    },
    label: {
      type: String,
    },
    tooltip: {
      type: String,
    },
    icon: {
      type: String,
    },
    iconButton: {
      type: String,
    },
    isButton: {
      type: Boolean,
      default: true,
    },
    isSwitch: {
      type: Boolean,
      default: false,
    },
    typeInput: {
      type: String,
      default: 'text',
    },
    maxlength: Number,
    formatInput: {
      type: Function,
    },
    allowOnlyLatin: {
      type: Boolean,
      default: false,
    },
    buttonColor: {
      type: String,
      default: '#85B285',
    },
    buttonDisabled: {
      type: Boolean,
      default: false,
    },
    selectItems: {
      type: Array,
      default: () => [],
    },
    selectItemText: {
      type: String,
      default: null,
    },
    selectItemValue: {
      type: String,
      default: null,
    },
    selectValue: {
      type: [String, Number, null],
      default: null,
    },
    selectWidth: {
      type: String,
      default: '90px',
    },
    containerWidth: {
      type: String,
      default: null,
    },
    inputWidth: {
      type: String,
      default: null,
    },
  },
  data () {
    return {
      localValue: this.value,
    };
  },
  watch: {
    value (newValue) {
      this.localValue = newValue;
    },
    localValue (newValue) {
      this.$emit('input', newValue);
    },
  },
  methods: {
    handleButtonClick () {
      if (this.buttonDisabled) return;
      this.$emit('button-click', this.value);
    },
    handleInput (event) {
      let value = event.target.value;
      if (this.allowOnlyLatin) {
        value = this.toLatinCarNumber(value);
        value = value.replace(/[^a-zA-Z0-9]/g, '');
      }
      if (typeof this.formatInput === 'function') {
        try {
          value = this.formatInput(value);
        } catch (e) {
        }
      }
      this.localValue = value;
      this.$emit('input', value);
    },
  },
  computed: {
    buttonInlineStyle () {
      return {
        backgroundColor: this.buttonColor,
      };
    },
    containerInlineStyle () {
      return this.containerWidth ? { width: this.containerWidth } : {};
    },
    inputInlineStyle () {
      return this.inputWidth ? { width: this.inputWidth } : {};
    },
    hasSelect () {
      return Array.isArray(this.selectItems) && this.selectItems.length > 0;
    },
    selectModel: {
      get () {
        return this.selectValue;
      },
      set (val) {
        this.$emit('update:selectValue', val);
      },
    },
  },
};
</script>

<style scoped>
.input-with-button {
  margin: 10px;
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  border: 1px solid #ccc;
  border-radius: 17px;
  min-width: 270px;
  height: 46px;
  background-color: #F8F8F8;
}

.icon-prefix {
  margin-left: 8px;
  display: flex;
  align-items: center;
}

.input-field {
  flex: 1 1 auto;
  border: none;
  outline: none;
  padding: 8px;
  font-size: 16px;
}

.inline-select {
  margin: 0 8px;
}

.no-spin::-webkit-outer-spin-button,
.no-spin::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.no-spin[type=number] {
  -moz-appearance: textfield;
}

.input-button {
  background-color: #85B285;
  border: none;
  border-radius: 16px;
  padding: 10px 22px;
  cursor: pointer;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
}

.input-button:hover {
  background-color: #70be73;
}
.input-button:disabled {
  background-color: #bdbdbd !important;
  cursor: not-allowed;
}
@media (max-width: 600px) {
  .input-with-button {
    width: 100%;
    flex-wrap: nowrap;
    overflow-x: auto;
  }

  .input-field {
    border-radius: 8px;
    font-size: 14px;
    width: auto !important;
    min-width: 70px;
    flex: 0 1 120px;
  }

  .inline-select {
    width: 70px !important;
    margin: 0 6px;
  }

  .input-button {
    border-radius: 12px;
    padding: 6px 10px;
    font-size: 18px;
    -webkit-appearance: none;
    appearance: none;
  }
}
@media (max-width: 325px) {
  .input-with-button {
    width: 250px;
  }
  .input-field {
    padding: 4px;
    width: 200px;
  }
}
</style>
